var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, InputAdornment, Typography } from '@mui/material';
import { times } from 'lodash';
import { Fragment, useCallback, useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RumEntityLabel } from '../../../observability/rumEntity';
import { FormField, NativeNumberInput, NumberInput, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, } from '../../common';
import { ExpandableRowLabel, TableHierarchyCell } from '../../common/tableHierarchy';
import { WizardContext } from '../context';
import { StyledCountryRow, StyledLocationTypeRow } from '../locationTable';
import { StyledSiteRow } from '../locationTable/styles';
import { useGroupedLocations, useLocationIndexMap, useQueueLocations } from '../selectors';
import { useWizardSimpleContext } from '../useWizardSimpleContext';
var PT_FULL_HOURS = '100%';
var PT_NAME_COLUMN_WIDTH = '30%';
var CENTERED_INPUT_PROPS = { style: { textAlign: 'center' } };
var DISABLED_PT_CELL_SX = {
    bgcolor: 'grey.300',
    color: 'text.secondary',
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiInputBase-root.Mui-disabled': { bgcolor: 'transparent' },
};
function buildFieldNames(prefix) {
    if (!prefix) {
        return {
            totalPartTime: 'totalPartTime',
            hoursPerDay: 'hoursPerDay',
            daysPerWeek: 'daysPerWeek',
            fteDay: 'fteDay',
        };
    }
    return {
        totalPartTime: "".concat(prefix, ".totalPartTime"),
        hoursPerDay: "".concat(prefix, ".hoursPerDay"),
        daysPerWeek: "".concat(prefix, ".daysPerWeek"),
        fteDay: "".concat(prefix, ".fteDay"),
    };
}
function usePartTimeBlur() {
    var setValue = useContext(WizardContext).methods.setValue;
    var watch = useFormContext().watch;
    return useCallback(function (blurredInputValue, prefix) {
        var fields = buildFieldNames(prefix);
        // If the raw input string was cleared or the watched value is 0, zero out dependents.
        var numericValue = blurredInputValue === '' ? 0 : Number(watch(fields.totalPartTime));
        if (numericValue === 0) {
            setValue(fields.hoursPerDay, 0, { shouldDirty: true });
            setValue(fields.daysPerWeek, 0, { shouldDirty: true });
            return;
        }
        // Otherwise, null-out a dependent field only if it is currently zero/empty
        // (forces the user to re-enter a meaningful value).
        var currentHours = watch(fields.hoursPerDay);
        var currentDays = watch(fields.daysPerWeek);
        if (!currentHours || Number(currentHours) === 0) {
            setValue(fields.hoursPerDay, null, { shouldDirty: true });
        }
        if (!currentDays || Number(currentDays) === 0) {
            setValue(fields.daysPerWeek, null, { shouldDirty: true });
        }
    }, [setValue, watch]);
}
var FteCell = function (_a) {
    var fields = _a.fields, disabled = _a.disabled, _b = _a.decimal, decimal = _b === void 0 ? 2 : _b;
    return (_jsx(TableCell, __assign({ align: "center", padding: "none" }, { children: _jsx(Box, __assign({ width: "100%" }, { children: _jsx(FormField, __assign({ name: fields.fteDay, disableHelperText: true }, { children: _jsx(NativeNumberInput, { max: 24, decimal: decimal, disabled: disabled }) })) })) })));
};
var PartTimeCells = function (_a) {
    var fields = _a.fields, disabled = _a.disabled, isPartTimeDisabled = _a.isPartTimeDisabled, onPartTimeBlur = _a.onPartTimeBlur;
    return (_jsxs(_Fragment, { children: [_jsx(TableCell, __assign({ align: "center", padding: "none" }, { children: _jsx(Box, __assign({ width: "100%" }, { children: _jsx(FormField, __assign({ required: true, name: fields.totalPartTime, disableHelperText: true }, { children: _jsx(NumberInput, { max: 100, decimal: 1, disabled: disabled, inputProps: CENTERED_INPUT_PROPS, fullWidth: true, InputProps: {
                                endAdornment: _jsx(InputAdornment, __assign({ position: "end" }, { children: "%" })),
                                onBlurCapture: function (e) { return onPartTimeBlur(e.target.value); },
                            } }) })) })) })), _jsx(TableCell, __assign({ align: "center", sx: isPartTimeDisabled ? DISABLED_PT_CELL_SX : undefined }, { children: PT_FULL_HOURS })), _jsx(TableCell, __assign({ align: "center", padding: "none", sx: isPartTimeDisabled ? DISABLED_PT_CELL_SX : undefined }, { children: _jsx(Box, __assign({ width: "100%" }, { children: _jsx(FormField, __assign({ required: true, name: fields.hoursPerDay, disableHelperText: true }, { children: _jsx(NumberInput, { min: isPartTimeDisabled ? 0 : 1, max: 24, decimal: 1, disabled: disabled || isPartTimeDisabled, inputProps: CENTERED_INPUT_PROPS, fullWidth: true }) })) })) })), _jsx(TableCell, __assign({ align: "center", padding: "none", sx: isPartTimeDisabled ? DISABLED_PT_CELL_SX : undefined }, { children: _jsx(Box, __assign({ width: "100%" }, { children: _jsx(FormField, __assign({ required: true, name: fields.daysPerWeek, disableHelperText: true }, { children: _jsx(NumberInput, { min: isPartTimeDisabled ? 0 : 1, max: 7, disabled: disabled || isPartTimeDisabled, inputProps: CENTERED_INPUT_PROPS, fullWidth: true }) })) })) }))] }));
};
var RowCells = function (_a) {
    var isFte = _a.isFte, rest = __rest(_a, ["isFte"]);
    return isFte ? (
    // Single-geo uses decimal=2; multi-geo uses decimal=1 (propagated via props)
    _jsx(FteCell, { fields: rest.fields, disabled: rest.disabled })) : (_jsx(PartTimeCells, __assign({}, rest)));
};
var WorkingDaysBaseTableHeader = function (_a) {
    var isFte = _a.isFte;
    var t = useTranslation('wizard', { keyPrefix: 'workingDay' }).t;
    var headers = isFte
        ? [t('workingDaysBaseTable.fteHeader')]
        : [
            t('workingDaysBaseTable.totalPTHeader'),
            t('workingDaysBaseTable.ptHoursHeader'),
            t('workingDaysBaseTable.hoursPerDayHeader'),
            t('workingDaysBaseTable.daysPerWeekHeader'),
        ];
    return (_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: isFte ? undefined : { width: PT_NAME_COLUMN_WIDTH } }), headers.map(function (col, index) { return (_jsx(TableCell, { children: col }, "".concat(col, "-").concat(index))); })] }) }));
};
var LocationDataRow = function (_a) {
    var location = _a.location, locIndexMap = _a.locIndexMap, disabled = _a.disabled, isFte = _a.isFte, handlePartTimeBlur = _a.handlePartTimeBlur;
    var watch = useFormContext().watch;
    var locIdx = locIndexMap.get(location.id);
    if (locIdx === undefined) {
        return null;
    }
    var prefix = "queueLocations.".concat(locIdx);
    var fields = buildFieldNames(prefix);
    var totalPartTime = watch(fields.totalPartTime);
    var isPartTimeDisabled = !totalPartTime || totalPartTime <= 0;
    return (_jsxs(StyledSiteRow, { children: [_jsx(TableHierarchyCell, __assign({ align: "left", nestLevel: 3 }, { children: _jsx(Typography, { children: _jsx(RumEntityLabel, __assign({ kind: "queueLocation", id: location.id }, { children: location.planLocation.locationName })) }) })), _jsx(RowCells, { isFte: isFte, fields: fields, disabled: disabled, isPartTimeDisabled: isPartTimeDisabled, onPartTimeBlur: function (value) { return handlePartTimeBlur(value, prefix); } })] }));
};
var CountryGroup = function (_a) {
    var country = _a.country, countryLocations = _a.countryLocations, emptyCellCount = _a.emptyCellCount, locIndexMap = _a.locIndexMap, disabled = _a.disabled, isFte = _a.isFte, handlePartTimeBlur = _a.handlePartTimeBlur;
    var _b = useState(false), closed = _b[0], setClosed = _b[1];
    return (_jsxs(_Fragment, { children: [_jsxs(StyledCountryRow, { children: [_jsx(TableHierarchyCell, __assign({ component: "th", align: "left", nestLevel: 2, sx: { py: 2 } }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: !closed, onToggle: function () { return setClosed(function (p) { return !p; }); }, bold: true }, { children: country })) })), times(emptyCellCount, function (i) { return (_jsx(TableCell, {}, i)); })] }), !closed &&
                countryLocations.map(function (location) { return (_jsx(LocationDataRow, { location: location, locIndexMap: locIndexMap, disabled: disabled, isFte: isFte, handlePartTimeBlur: handlePartTimeBlur }, location.id)); })] }));
};
var LocationTypeGroup = function (_a) {
    var locationType = _a.locationType, countries = _a.countries, emptyCellCount = _a.emptyCellCount, props = __rest(_a, ["locationType", "countries", "emptyCellCount"]);
    var t = useTranslation('wizard').t;
    var _b = useState(false), closed = _b[0], setClosed = _b[1];
    var locationTypeKey = locationType;
    return (_jsxs(Fragment, { children: [_jsxs(StyledLocationTypeRow, { children: [_jsx(TableHierarchyCell, __assign({ component: "th", align: "left", nestLevel: 1, sx: { py: 2 } }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: !closed, onToggle: function () { return setClosed(function (p) { return !p; }); }, bold: true }, { children: t("message.".concat(locationTypeKey)) })) })), times(emptyCellCount, function (i) { return (_jsx(TableCell, {}, i)); })] }), !closed &&
                Object.entries(countries).map(function (_a) {
                    var country = _a[0], countryLocations = _a[1];
                    return (_jsx(CountryGroup, __assign({ country: country, countryLocations: countryLocations, emptyCellCount: emptyCellCount }, props), country));
                })] }));
};
var WorkingDaysBaseTableBody = function (_a) {
    var _b;
    var disabled = _a.disabled, _c = _a.isFte, isFte = _c === void 0 ? false : _c;
    var watch = useFormContext().watch;
    var queue = ((_b = useWizardSimpleContext()) !== null && _b !== void 0 ? _b : {}).queue;
    var handlePartTimeBlur = usePartTimeBlur();
    var locations = useQueueLocations();
    var groupedLocations = useGroupedLocations();
    var locIndexMap = useLocationIndexMap();
    if (locations.length === 0) {
        var fields = buildFieldNames(); // no prefix -> single-geo field names
        var totalPartTime = watch(fields.totalPartTime);
        var isPartTimeDisabled = !totalPartTime || totalPartTime <= 0;
        return (_jsx(TableBody, { children: _jsxs(TableRow, { children: [_jsx(TableCell, __assign({ align: "left", component: "th" }, { children: _jsx(Typography, { children: _jsx(RumEntityLabel, __assign({ kind: "queue", id: queue === null || queue === void 0 ? void 0 : queue.id }, { children: queue === null || queue === void 0 ? void 0 : queue.name })) }) })), _jsx(RowCells, { isFte: isFte, fields: fields, disabled: disabled, isPartTimeDisabled: isPartTimeDisabled, onPartTimeBlur: function (value) { return handlePartTimeBlur(value); } })] }) }));
    }
    var emptyCellCount = isFte ? 1 : 4;
    return (_jsx(TableBody, { children: Object.entries(groupedLocations).map(function (_a) {
            var locationType = _a[0], countries = _a[1];
            return (_jsx(LocationTypeGroup, { locationType: locationType, countries: countries, emptyCellCount: emptyCellCount, locIndexMap: locIndexMap, disabled: disabled, isFte: isFte, handlePartTimeBlur: handlePartTimeBlur }, locationType));
        }) }));
};
export var WorkingDaysBaseTable = function (_a) {
    var disabled = _a.disabled, isFte = _a.isFte;
    return (_jsx(TableContainer, { children: _jsxs(Table, __assign({ sx: { tableLayout: 'fixed' } }, { children: [_jsx(WorkingDaysBaseTableHeader, { isFte: isFte }), _jsx(WorkingDaysBaseTableBody, { disabled: disabled, isFte: isFte })] })) }));
};
